import { createContext, FunctionComponent } from 'preact';
import { useCallback, useContext, useMemo, useState } from 'preact/hooks';
import { RequestRejectionError } from '##/services/tenApi';
import { PageNavigation } from '##/utils/PageNavigation';
import { Messenger } from '##/utils/Messenger';
import {
  TealiumErrorCode,
  TealiumErrorData,
  TealiumErrorContext,
} from '##/services/analytics/Tealium/TealiumErrorTypes';
import { ErrorScreenContextValue, ErrorScreenState, ErrorType } from './types';

export const ErrorScreenContext = createContext<
  ErrorScreenContextValue | undefined
>(undefined);

export const tealiumTrackError$ = new Messenger<TealiumErrorData>({
  errorCode: TealiumErrorCode.OztamPostRequest,
  errorDetail: '',
  errorSource: '',
  errorMessage: '',
  errorContext: TealiumErrorContext.API,
});

export const sendErrorToTealium = (errorData: TealiumErrorData): void => {
  tealiumTrackError$.send(errorData);
};

export const ErrorScreenProvider: FunctionComponent<{}> = (props) => {
  const [errorScreen, setErrorScreen] = useState<ErrorScreenState>({
    layout: 'NO_ERROR',
  });

  const hasError = useMemo((): boolean => errorScreen.layout === 'ERROR_PAGE', [
    errorScreen,
  ]);

  const showError = useCallback((error: ErrorType) => {
    setErrorScreen({ layout: 'ERROR_PAGE', error });
  }, []);

  const clear = useCallback(() => {
    setErrorScreen({ layout: 'NO_ERROR' });
  }, []);

  const catchApiError = useCallback((error: RequestRejectionError) => {
    showError(error.internalErrorType);
  }, []);

  if (errorScreen.layout === 'ERROR_PAGE') {
    PageNavigation.replaceRoute('/error', { error: errorScreen.error });
  }

  return (
    <ErrorScreenContext.Provider
      value={{ clear, showError, catchApiError, hasError, sendErrorToTealium }}
    >
      {props.children}
    </ErrorScreenContext.Provider>
  );
};

export const useErrorScreen = (): ErrorScreenContextValue => {
  const context = useContext(ErrorScreenContext);

  if (!context) {
    throw new Error('Component beyond ErrorScreenContext');
  }

  return context;
};
